// ─────────────────────────────────────────────────────────────────────────────
// The locale registry — the one place that knows which languages exist.
//
// Adding a language is meant to be a data change, not a code change: append an
// entry here, drop a folder under `translations/`, register its loaders in
// loader.js, and every picker, formatter and direction switch picks it up. No
// component, screen or service is allowed to branch on a locale id — if a
// behaviour differs per language it belongs in the descriptor below.
//
// Ported from the doctor app. The clinic panel ships fa, en and ar. The
// OPTIONAL descriptor fields below are what make a language a data change
// rather than a refactor — `ar` declares four of them, because ICU's data for
// Arabic on the JALALI calendar is wrong in four separate ways and every one of
// them looks like plausible Arabic in review. See docs/I18N.md, and the doctor
// app's §"When CLDR is wrong", before adding a fourth language.
//
// THE BRAND (@parsina/framework/ui/lib/brand, configured at boot by
// src/lib/brandBoot.js) narrows this list and moves its defaults, never the
// descriptors' data: `locales` decides which of these languages are OFFERED
// (`isOfferedLocale` — every resolver below asks it, so a language the brand
// does not offer is never started in, cached or adopted from the server), and
// `defaultLocale` is the first-visit language. `calendar` swaps each
// descriptor's calendar through the descriptor's own `calendars` data. With no
// brand all three are exactly what they were. Same rules as the framework's
// clinical/src/i18n/config.js, which the visit room reads.
// ─────────────────────────────────────────────────────────────────────────────

import { brandLocales, brandDefaultLocale, calendarSystem } from '@parsina/framework/ui/lib/brand';

/**
 * A locale descriptor.
 *
 *  id       BCP-47 tag. Also the folder name under `translations/` and the value
 *           persisted as the signed-in user's `locale` in Keycloak.
 *  label    The language's name IN THAT LANGUAGE. A picker must be readable by
 *           someone who cannot read the language currently on screen, which is
 *           exactly the person using it, so this is never translated.
 *  dir      Writing direction — drives <html dir> and every logical CSS property.
 *  intl     The tag handed to Intl.*. Carries the calendar and numbering system
 *           as Unicode extensions, so one string configures every formatter and
 *           no formatter ever branches on `id`.
 *  timeZone Clinics run on Iran local time regardless of UI language: an
 *           appointment at 14:30 is 14:30 in Tehran, not in the browser's zone.
 *           A cashier shift opened at 08:00 closes on the same clock in every
 *           language, which is what makes a shift report reconcilable.
 *
 * The OPTIONAL fields below exist so that a locale whose CLDR data or whose
 * reader differs from the default can say so as DATA. They are the escape hatch
 * that keeps the rest of the app free of `if (locale === …)`; a locale that
 * omits them gets the default behaviour.
 *
 *  dateStyles       Per-style overrides merged over the shared DATE_STYLES.
 *  stripEra         Drop the era designator ICU appends to a non-default
 *                   calendar (Arabic on the Persian calendar gets «ه‍.ش»).
 *  numericDateOrder Field order for the all-numeric date shapes, e.g.
 *                   ['year','month','day']. Applied only when the formatted
 *                   date really is all-numeric, so a month NAME is never
 *                   reordered.
 *  readsPersianData Whether a reader of this language can read the Persian
 *                   REFERENCE DATA the clinic stores — service names, item
 *                   names, supplier names, seeded role templates. Those are
 *                   entered by the clinic and are not translated. It is a
 *                   property of the READER, not of the direction or the
 *                   calendar: Arabic is right-to-left like Persian and still
 *                   cannot read it.
 *  monthNames       Twelve month names, index 0 = the calendar's first month,
 *                   replacing ICU's. For when CLDR's data for the pairing of
 *                   this language with this calendar is simply wrong.
 *  clinicCalendarIntl
 *                   The tag for surfaces that are JALALI in every language, not
 *                   just in the ones whose own calendar is Jalali. The clinic
 *                   panel has several: an accounting period, a shift report's
 *                   month header and the reports date range are Jalali because
 *                   the clinic's books are.
 *  calendar         The calendar `intl` is on: 'jalali' or 'gregory'.
 *  calendars        What changes when a BRAND puts every language on one
 *                   calendar (`calendar` in the brand): fields merged over this
 *                   descriptor, keyed by that calendar — also the locale's own,
 *                   where a Jalali-in-every-language surface stops being Jalali.
 *                   A field set to `undefined` is a correction that does not
 *                   apply there — the Jalali month names make no sense on a
 *                   Gregorian date.
 */
export const LOCALES = {
  // i18n-exempt: a locale's `label` is its ENDONYM — the name the language
  // gives itself — so it is deliberately not translated. «فارسی» stays «فارسی»
  // in the English UI, the same way «English» stays «English» in the Persian
  // one; that is what makes a language switcher usable to someone who cannot
  // read the current language. `weekdaySeparator` is likewise locale data.
  fa: {
    id: 'fa',
    label: 'فارسی',
    englishLabel: 'Persian',
    dir: 'rtl',
    // -u-ca-persian → Jalali (Shamsi) calendar; -u-nu-arabext → Persian digits.
    intl: 'fa-IR-u-ca-persian-nu-arabext',
    calendar: 'jalali',
    calendars: {
      gregory: { intl: 'fa-IR-u-ca-gregory-nu-arabext' },
    },
    // The DEFAULT only — before a clinic is known. Under the clinic context every
    // date is formatted on the CLINIC's zone (Membership.timezone, PM-0088).
    timeZone: 'Asia/Tehran',
    // What goes between the weekday and the rest of a long date. See the
    // `full` style in format.js for why this is spelled out here rather than
    // left to `dateStyle: 'full'`.
    weekdaySeparator: '، ',
    // Persian is the only language that reads the clinic's own stored names.
    readsPersianData: true,
  },
  en: {
    id: 'en',
    label: 'English',
    englishLabel: 'English',
    dir: 'ltr',
    intl: 'en-US',
    calendar: 'gregory',
    calendars: {
      // A brand on the Jalali calendar everywhere. ICU appends the era ("AP")
      // to a calendar that is not the language's own.
      jalali: { intl: 'en-US-u-ca-persian', stripEra: true },
      // A brand on the Gregorian calendar keeps no Jalali books: the surfaces
      // below that are Jalali in every language read Gregorian months there.
      gregory: { clinicCalendarIntl: undefined },
    },
    // The DEFAULT only — before a clinic is known. Under the clinic context every
    // date is formatted on the CLINIC's zone (Membership.timezone, PM-0088).
    timeZone: 'Asia/Tehran',
    weekdaySeparator: ', ',
    // English dates are Gregorian, but a few surfaces are Jalali in EVERY
    // language because the clinic itself is — an accounting period and a shift
    // report are bounded by Jalali months, so labelling those «August» would
    // name a month the period does not start in. ICU renders the Jalali months
    // in Latin for this tag: «Farvardin», «Mordad».
    clinicCalendarIntl: 'en-US-u-ca-persian',
  },
  // i18n-exempt: endonym and locale data, exactly as for `fa` above — «العربية»
  // is the name Arabic gives itself and stays «العربية» in every UI language.
  // A second marker is needed because the first one's exemption ends with the
  // `fa` descriptor it introduces, and the audit cannot tell Arabic script from
  // Persian: both live in U+0600–U+06FF.
  ar: {
    id: 'ar',
    label: 'العربية',
    englishLabel: 'Arabic',
    dir: 'rtl',
    // JALALI, not Gregorian. An Iranian clinic runs on the Jalali calendar —
    // the day sheet, the appointment book and the accounting period are all
    // Jalali — and the date PICKER (@parsina/framework JalaliDatePicker, which
    // follows <html lang> since v0.19.0) draws Arabic on the Jalali calendar
    // too, to match this tag. Handing Arabic `ar-EG` here would
    // mean a receptionist picks ۱۴۰۴/۰۵/۱۲ and reads back «٣ أغسطس ٢٠٢٥»: the
    // same class of transcription error the 24-hour clock rule in format.js
    // exists to prevent. -u-nu-arab → Arabic-Indic digits (٠١٢…), which is what an
    // Arabic reader expects and is a DIFFERENT set from Persian's arabext.
    intl: 'ar-u-ca-persian-nu-arab',
    calendar: 'jalali',
    calendars: {
      // On the Gregorian calendar two corrections below are Jalali-only: the
      // month names are Jalali names, and day-first is how Arabic writes a
      // Gregorian date.
      gregory: {
        intl: 'ar-u-ca-gregory-nu-arab',
        monthNames: undefined,
        numericDateOrder: undefined,
      },
    },
    // The DEFAULT only — before a clinic is known. Under the clinic context every
    // date is formatted on the CLINIC's zone (Membership.timezone, PM-0088).
    timeZone: 'Asia/Tehran',
    weekdaySeparator: '، ',
    // ICU's Arabic patterns for the Persian calendar disagree with the clinic's
    // conventions in three ways. Correcting them here, as data, is what keeps
    // format.js free of locale ids — see the descriptor notes above.
    dateStyles: {
      // ICU gives Arabic an ALL-NUMERIC medium («١٢‏/٠٥‏/١٤٠٤») where both other
      // locales give a month name («۱۲ مرداد ۱۴۰۴», «Aug 3, 2025»). `medium` is
      // the default shape, on nearly every row in the app, so it is the one
      // that has to stay readable.
      medium: { day: 'numeric', month: 'long', year: 'numeric' },
    },
    // ICU appends «ه‍.ش» (with a ZWJ) to every Arabic-on-Persian-calendar shape
    // but `dayMonth`. It is noise next to a Persian date that never carries it,
    // and `era: undefined` does not suppress it.
    stripEra: true,
    // ICU orders the numeric Arabic date day-first («١٢/٥/١٤٠٤») where Persian
    // writes it year-first («۱۴۰۴/۵/۱۲»). Two RTL languages disagreeing about
    // the same calendar on the same screen is how a date gets transcribed wrong.
    numericDateOrder: ['year', 'month', 'day'],
    // CLDR's ARABIC month names for the Persian calendar are corrupted: six of
    // the twelve are not words in any language. Verified against ICU 78, and
    // identical for every ar-* region (ar, ar-EG, ar-IQ, ar-SA):
    //
    //   ICU says   فرفردن · أذربيهشت · تار · شهرفار · آيان · اسفندار
    //   should be  فروردين · ارديبهشت · تير · شهريور · آبان · اسفند
    //
    // A month name sits on every visit row, every day sheet and every printed
    // prescription, so this cannot be left to upstream. Index 0 = فروردين.
    monthNames: [
      'فروردين', 'ارديبهشت', 'خرداد', 'تير', 'مرداد', 'شهريور',
      'مهر', 'آبان', 'آذر', 'دي', 'بهمن', 'اسفند',
    ],
  },
};

export const LOCALE_IDS = Object.keys(LOCALES);

/** The product default: what the clinic panel is, absent any signal. */
export const DEFAULT_LOCALE = 'fa';

/**
 * Where a missing translation key is looked up next, and the last resort when
 * the device language is not one we ship. English — it is the language every
 * key is guaranteed to exist in, because `en` is the source catalogue that `fa`
 * is translated from.
 */
export const FALLBACK_LOCALE = 'en';

/**
 * Translation namespaces — one file per feature/module, per locale.
 *
 * The first segment of every key IS the namespace: `t('cashier.shift.open')`
 * reads `translations/<locale>/cashier.json`. That makes a key self-locating —
 * you can find the string a screen shows without knowing anything about how the
 * loader works.
 *
 * These are the CLINIC's modules, not the doctor app's. The clinic panel is the
 * operational back office — who walks in, what they owe, who collected it, what
 * it consumed from stock, and who may do any of it.
 */
export const NAMESPACES = [
  'common',       // buttons, generic nouns, units, status words — used everywhere
  'nav',          // sidebar, top bar, document titles
  'validation',   // field-level validation messages
  'errors',       // failure messages, error states, empty states
  'auth',         // sign-in, onboarding, joining a clinic by invitation code
  'shortcuts',    // the keyboard-shortcut help overlay (`?`)
  'settings',     // the settings screen, including the language picker
  'reception',    // reception desk: today's sheet, walk-ins, the queue
  'patients',     // patient list, profile, account drawer
  'calendar',     // the appointment calendar
  'doctors',      // clinic doctors: membership, presence hours, contracts
  'people',       // staff, roles, the permission catalog
  'payroll',      // «حقوق و دستمزد» — employment contracts and the monthly run
  'cashier',      // the settle drawer's money vocabulary — methods, statuses,
                  //   line-item validation. Named for «صندوق», which was removed
                  //   as a page on 2026-09-12; the keys outlived it because the
                  //   settle flow did (پذیرش opens the same drawer)
  'payments',     // patient payments and their gateways
  'accounting',   // invoices, patient accounts, doctor share
  'insurance',    // insurers, patient policies, coverage rules, claims
  'operations',   // «مدیریت هزینه‌ها» — the expense ledger and bills; also the
                  //   taxonomies, stock states and status vocabularies BOTH it and
                  //   «کالاها و خدمات» render, which is why the namespace kept
                  //   its name through the 2026-09-12 split
  'goods',        // «کالاها و خدمات» — that page's own chrome only
  'store',        // «فروشگاه» — the clinic store, its listings, orders and the
                  //   Marketplace vocabulary
  'reports',      // clinic reports
  'subscription', // the clinic's own Parsina subscription
  'clinic',       // «کلینیک من» — clinic identity, patient join code, subscription
  'secretary',    // «منشی هوشمند پارسینا» — the voice-command overlay (PM-0080)
];

export const isSupportedLocale = (id) => Object.prototype.hasOwnProperty.call(LOCALES, id);

/** The ids a person may choose: the shipped ones, narrowed to the brand's `locales`. */
export const offeredLocaleIds = () => brandLocales(LOCALE_IDS);

/** Shipped AND offered by the brand. Without a brand, the same as `isSupportedLocale`. */
export const isOfferedLocale = (id) => isSupportedLocale(id) && offeredLocaleIds().includes(id);

/**
 * The descriptors a language picker lists — `LOCALES` itself unless the brand
 * offers fewer.
 */
export function offeredLocales() {
  const ids = offeredLocaleIds();
  if (ids.length === LOCALE_IDS.length) return LOCALES;
  return Object.fromEntries(ids.map((id) => [id, LOCALES[id]]));
}

/**
 * The language for someone the panel knows nothing about: DEFAULT_LOCALE, or
 * the brand's `defaultLocale` (and never one the brand does not offer).
 */
export const productDefaultLocale = () => brandDefaultLocale(DEFAULT_LOCALE, LOCALE_IDS);

/**
 * The descriptor for a locale, or the product default's if the id is unknown —
 * on the brand's calendar when the brand names one (the descriptor's own
 * `calendars` data); otherwise the descriptor itself, untouched.
 */
export const localeDescriptor = (id) => {
  const descriptor = LOCALES[id] || LOCALES[productDefaultLocale()];
  const calendar = calendarSystem(null);
  if (!calendar) return descriptor;
  // Applied even on the locale's own calendar: a Gregorian brand has no Jalali
  // clinic months, in English too (`en`'s `calendars.gregory`).
  const override = descriptor.calendars?.[calendar];
  if (!override && calendar === descriptor.calendar) return descriptor;
  return { ...descriptor, ...override, calendar };
};

/**
 * Narrow a BCP-47 tag to a locale we ship and the brand offers: `en-GB` → `en`,
 * `fa-IR` → `fa`. Returns null for anything else, so callers can tell "no
 * match" from "matched the default".
 */
export function normalizeLocale(tag) {
  if (!tag) return null;
  const raw = String(tag).trim();
  const base = raw.toLowerCase().split(/[-_]/)[0];
  const id = isSupportedLocale(raw) ? raw : (isSupportedLocale(base) ? base : null);
  return id && isOfferedLocale(id) ? id : null;
}

// ── Persistence ───────────────────────────────────────────────────────────────
//
// localStorage is a CACHE, not the record. The record is the signed-in user's
// `locale` attribute in Keycloak, written by `updateMyPreferences` and read
// back by `myProfile` — which is what makes the choice follow a staff member to
// the second reception workstation, and what makes the SIGN-IN page follow too
// (`locale` is Keycloak's own attribute, so the login theme honours it without
// this app doing anything).
//
// The cache exists so a reload paints the right language and direction
// immediately instead of flashing the default while `myProfile` is in flight.

export const STORAGE_KEY = 'parsina.locale';

export function readStoredLocale() {
  try {
    return normalizeLocale(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private mode, disabled storage, or no window at all (tests, SSR).
    return null;
  }
}

export function storeLocale(id) {
  try {
    if (isOfferedLocale(id)) window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* A locale we cannot cache still works — it just re-resolves next launch. */
  }
}

/**
 * The language to start in:
 *
 *   1. what the user last chose (cached from their Keycloak `locale`)
 *   2. DEFAULT_LOCALE
 *
 * There used to be a `navigator.language` step between the two, and it was
 * wrong for this product in a way worth spelling out, because it looks correct
 * everywhere else.
 *
 * Parsina is an Iranian medical platform: every clinic, every receptionist and
 * every patient is in Iran, and the panel's own reference data — service names,
 * item names, role templates — is stored in Persian and never translated. But
 * Windows and Chrome in Iran are overwhelmingly installed with an en-US locale,
 * so `navigator.languages` reports `['en-US']` for a Persian-speaking
 * receptionist who has expressed no preference at all. Device detection cannot
 * tell that apart from a real choice of English, so it read a setup artifact as
 * a preference and opened the panel in English — LTR, Gregorian, and unreadable
 * to the person using it.
 *
 * Persian is therefore the floor, not a guess: `DEFAULT_LOCALE` is what this
 * product IS absent a signal. A real preference still wins outright via step 1,
 * which is fed by the picker in حساب پارسینا and by the `locale` attribute on
 * the signed-in user — a stated choice, not an inferred one. `FALLBACK_LOCALE`
 * keeps its own separate job: where a MISSING KEY is looked up, which must stay
 * English because `en` is the source catalogue every key is guaranteed to
 * exist in.
 *
 * A brand moves step 2 to its own `defaultLocale` (`productDefaultLocale`), and
 * step 1 only ever answers a language the brand offers (`normalizeLocale`), so a
 * cached choice the brand does not offer falls through to step 2. index.html's
 * pre-paint script makes the same two moves; keep them in step.
 */
export function resolveInitialLocale() {
  return readStoredLocale() || productDefaultLocale();
}

/**
 * Push a locale onto the document: `<html lang dir>`.
 *
 * `dir` is what makes RTL work — every logical CSS property (margin-inline-*,
 * padding-inline-*, inset-inline-*, text-align: start/end) resolves against it,
 * so this single attribute mirrors the whole layout. Nothing else in the app
 * should ever set `direction` on a container.
 *
 * `lib/auth.js` reads `<html lang>` back through `panelLocale()` to tell
 * Keycloak which language to serve the sign-in page in, so this attribute is
 * also what makes the login screen follow the panel.
 *
 * Returns the id actually applied, so callers can trust the result over their
 * input when the input was junk.
 */
export function applyDocumentLocale(id) {
  const locale = isOfferedLocale(id) ? id : productDefaultLocale();
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (root) {
    root.setAttribute('lang', locale);
    root.setAttribute('dir', LOCALES[locale].dir);
  }
  return locale;
}
